import { useRef } from 'react';
import useCOS from '@/pages/aigc/pages/ResourceUpload/components/FormUpload/useCOS';
import { fetchCOSInfo, doUpload } from '../services';
import { getFileSimpleName } from '../utils';
import { UploadStatus } from '../enums';

import type { IFilter, IVideo, IFetchCOSInfoResData } from '../types';

export default function useUploadEffect() {
  // 本次上传的cos配置信息
  const cosInfo = useRef<IFetchCOSInfoResData>({
    region: '',
    bucket: '',
    tmp_secret_id: '',
    tmp_secret_key: '',
    session_token: '',
    uuid: '',
    path: '',
  });

  // 模拟获取COS配置信息
  const fetchCOSInfoMock = async () => Promise.resolve({ ...(cosInfo.current || {}) });

  const { putObject } = useCOS({
    scopeLimit: true,
    getCOSConfig: fetchCOSInfoMock,
  });

  const executeUploadTask = async (
    file: File,
    video: IVideo,
    filter: IFilter,
    options: {
      callback: (uuid: string, payload: Partial<IVideo>) => void;
    },
  ): Promise<void> => {
    const { role_model_type: roleModelType } = filter || {};
    const { callback } = options || {};

    try {
      // 获取COS相关配置信息，及本次上传视频的uuid、上传路径
      const {
        tmp_secret_id: tmpSecretId,
        tmp_secret_key: tmpSecretKey,
        session_token: sessionToken,
        region,
        bucket,
        uuid,
        path,
      } = await fetchCOSInfo();

      const fullPath = path?.endsWith('/') ? `${path}${file?.name}` : `${path}/${file?.name}`;

      // 同步保存 本次上传的cos配置信息
      Object.assign(cosInfo.current, {
        region,
        bucket,
        tmp_secret_id: tmpSecretId,
        tmp_secret_key: tmpSecretKey,
        session_token: sessionToken,
        uuid,
        path,
      });

      // 上传视频文件到腾讯云
      await putObject({
        region: cosInfo.current?.region,
        bucket: cosInfo.current?.bucket,
        path: fullPath,
        file,
      });

      // 上传视频信息到后端
      await doUpload({
        uuid,
        origin_video_name: getFileSimpleName(file?.name),
        origin_video: fullPath,
        origin_video_time: video?.origin_video_time,
        origin_video_md5: '',
        role_model_type: roleModelType,
      });

      callback?.(video?.uuid, {
        uuid,
        status: UploadStatus.Processing,
      });
    } catch (exception: any) {
      callback?.(video?.uuid, {
        status: UploadStatus.Error,
        result_msg: exception?.message,
      });
    }
  };

  const handleUpload = async (
    files: File[],
    videos: IVideo[],
    filter: IFilter,
    options: {
      callback: (uuid: string, payload: Partial<IVideo>) => void;
    },
  ) => {
    const uploadTasks: Promise<void>[] = [];

    for (let i = 0;i < files?.length;i++) {
      const file = files?.[i];
      const video = videos?.[i];
      const uploadTask = executeUploadTask(file, video, filter, options);
      uploadTasks.push(uploadTask);
    }

    Promise.all(uploadTasks);
  };

  return {
    onUpload: handleUpload,
  };
}
